import { z } from 'zod';

import { assert } from '@libraries/typescript/utilities';

const runtimeUrlSchema = z.union([
    z.url(),
    z.string().regex(/^\/.+/, 'Expected an absolute URL or root-relative path'),
]);

const LOOPBACK_HOSTNAMES = new Set(['127.0.0.1', 'localhost']);
const LOOPBACK_URL_PATTERN =
    /^([a-z][a-z\d+.-]*:\/\/(?:[^/?#]*@)?)(?:localhost|127\.0\.0\.1)(?=[:/?#]|$)/i;

/**
 * Non-sensitive environment-specific config that is served with the web app.
 *
 * The values are defined in `apps/web/config/<environment>.json`.
 *
 * Values that are not required in every environment should be nullable, not
 * optional.
 */
export const runtimeConfigSchema = z
    .object({
        runEnvironment: z.enum(['development', 'staging', 'production']),
        sha: z.string(),
        branch: z.string().nullable(),
        bundleHash: z.string(),
        apiUrl: z.url(),
        kratosPublicUrl: runtimeUrlSchema,
        agentationEndpoint: z.url().nullable(),
        simulatorWorldUrl: runtimeUrlSchema.nullable(),

        sentryDsn: z.url().nullable(),
        datadogClientToken: z.string().nullable(),
        datadogRumApplicationId: z.string().nullable(),
        datadogRumClientToken: z.string().nullable(),
        datadogSite: z.string().nullable(),
        intercomAppId: z.string().nullable(),
        posthogKey: z.string().nullable(),
    })
    .strict();

type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;
type RunEnvironment = RuntimeConfig['runEnvironment'];

let runtimeConfig: RuntimeConfig | null = null;
let promise: Promise<RuntimeConfig> | null = null;

export function alignLoopbackApiUrl(
    apiUrl: string,
    pageHostname: string,
): string {
    if (!LOOPBACK_HOSTNAMES.has(pageHostname)) {
        return apiUrl;
    }

    let apiHostname: string;
    try {
        apiHostname = new URL(apiUrl).hostname;
    } catch {
        return apiUrl;
    }

    if (!LOOPBACK_HOSTNAMES.has(apiHostname) || apiHostname === pageHostname) {
        return apiUrl;
    }

    return apiUrl.replace(LOOPBACK_URL_PATTERN, `$1${pageHostname}`);
}

export function initializeRuntimeConfig(): Promise<RuntimeConfig> {
    if (!promise) {
        promise = fetch('/config.json', { cache: 'no-store' })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`/config.json ${response.status}`);
                }
                return response.json();
            })
            .then((json) => {
                runtimeConfig = runtimeConfigSchema.parse(json);
                return runtimeConfig;
            });
    }
    return promise;
}

export function getRuntimeConfig(): RuntimeConfig {
    assert.isNotNull(runtimeConfig, 'Runtime config has not been initialized');
    return {
        ...runtimeConfig,
        apiUrl: alignLoopbackApiUrl(
            runtimeConfig.apiUrl,
            window.location.hostname,
        ),
    };
}

/**
 * The running build's commit SHA, or null before the config has loaded.
 *
 * Callers that key shared browser state on the build need the SHA without
 * asserting that boot has finished: they run inside React and must be able to
 * degrade rather than throw when they render early.
 */
export function getBuildShaIfInitialized(): string | null {
    return runtimeConfig?.sha ?? null;
}

export function getRunEnvironment(): RunEnvironment {
    assert.isNotNull(runtimeConfig, 'Runtime config has not been initialized');
    return runtimeConfig.runEnvironment;
}
